import { prefersReducedMotion } from '@theme/utilities';

// Min intersectionRatio to start reveal: desktop vs mobile (≤768px). Same rootMargin for both (-12% bottom).
/** @type {number} */
const VISIBILITY_THRESHOLD_DESKTOP = 0.25;
/** @type {number} */
const VISIBILITY_THRESHOLD_MOBILE = 0.70;
/** IntersectionObserver threshold steps — must include both visibility thresholds so callbacks fire when crossing them. */
const INTERSECTION_THRESHOLDS = [0, VISIBILITY_THRESHOLD_DESKTOP, VISIBILITY_THRESHOLD_MOBILE, 1];
/** @type {number} */
const DELAY_BETWEEN_GROUPS_MS = 120;
/** @type {number} */
const DELAY_BETWEEN_BADGES_MS = 90;

/**
 * @param {number} ms
 * @returns {Promise<void>}
 */
function delay(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * @returns {boolean}
 */
function isMobileViewport() {
  return window.matchMedia('(max-width: 768px)').matches;
}

/**
 * @returns {number}
 */
function getVisibilityThreshold() {
  return isMobileViewport() ? VISIBILITY_THRESHOLD_MOBILE : VISIBILITY_THRESHOLD_DESKTOP;
}

/**
 * @param {HTMLElement | null} el
 */
function reveal(el) {
  if (el) el.classList.add('is-reveal');
}

/**
 * @param {HTMLElement | null} el
 */
function hide(el) {
  if (el) el.classList.remove('is-reveal');
}

/**
 * @param {HTMLElement} section
 * @returns {HTMLElement[]}
 */
function getRevealElements(section) {
  const title = section.querySelector('.sneaky-good__title');
  const subtitle = section.querySelector('.sneaky-good__subtitle');
  const content = section.querySelector('.sneaky-good__content');
  const badges = /** @type {HTMLElement[]} */ ([...section.querySelectorAll('.sneaky-good__badge')]);
  const elements = section.querySelector('.sneaky-good__elements');

  return [
    title instanceof HTMLElement ? title : null,
    subtitle instanceof HTMLElement ? subtitle : null,
    content instanceof HTMLElement ? content : null,
    ...badges,
    elements instanceof HTMLElement ? elements : null
  ].filter((el) => el instanceof HTMLElement);
}

/**
 * @param {HTMLElement} section
 */
function resetRevealState(section) {
  getRevealElements(section).forEach((el) => hide(el));
}

/**
 * @param {HTMLElement} section
 * @param {number} runId
 */
async function runRevealSequence(section, runId) {
  const shouldStop = () => Number(section.dataset.sneakyRevealRunId || '0') !== runId;
  const titleNode = section.querySelector('.sneaky-good__title');
  const subtitleNode = section.querySelector('.sneaky-good__subtitle');
  const contentNode = section.querySelector('.sneaky-good__content');
  const title = titleNode instanceof HTMLElement ? titleNode : null;
  const subtitle = subtitleNode instanceof HTMLElement ? subtitleNode : null;
  const content = contentNode instanceof HTMLElement ? contentNode : null;
  const badges = /** @type {HTMLElement[]} */ ([...section.querySelectorAll('.sneaky-good__badge')]);
  const elements = section.querySelector('.sneaky-good__elements');

  if (prefersReducedMotion()) {
    reveal(title);
    reveal(subtitle);
    reveal(content);
    badges.forEach((b) => reveal(b));
    reveal(elements instanceof HTMLElement ? elements : null);
    return;
  }

  if (shouldStop()) return;
  reveal(title);
  if (shouldStop()) return;
  reveal(subtitle);
  await delay(DELAY_BETWEEN_GROUPS_MS);
  if (shouldStop()) return;

  reveal(content);
  await delay(DELAY_BETWEEN_GROUPS_MS);
  if (shouldStop()) return;

  for (const badge of badges) {
    if (shouldStop()) return;
    reveal(badge);
    await delay(DELAY_BETWEEN_BADGES_MS);
  }

  await delay(DELAY_BETWEEN_GROUPS_MS);
  if (shouldStop()) return;
  reveal(elements instanceof HTMLElement ? elements : null);
}

/**
 * @param {HTMLElement} section
 */
function initSneakyGood(section) {
  if (!(section instanceof HTMLElement)) return;
  if (section.dataset.sneakyRevealInit === 'true') return;
  section.dataset.sneakyRevealInit = 'true';
  section.dataset.sneakyRevealRunId = '0';
  section.dataset.sneakyRevealActive = 'false';

  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries.find((item) => item.target === section);
      if (!entry) return;
      const minRatio = getVisibilityThreshold();

      // Only reset/hide when section is fully out of view.
      if (!entry.isIntersecting || entry.intersectionRatio <= 0) {
        section.dataset.sneakyRevealActive = 'false';
        section.dataset.sneakyRevealRunId = String(Number(section.dataset.sneakyRevealRunId || '0') + 1);
        resetRevealState(section);
        return;
      }

      // Not enough visible yet — keep current state (do not hide early).
      if (entry.intersectionRatio < minRatio) return;

      if (section.dataset.sneakyRevealActive === 'true') return;
      section.dataset.sneakyRevealActive = 'true';
      const nextRunId = Number(section.dataset.sneakyRevealRunId || '0') + 1;
      section.dataset.sneakyRevealRunId = String(nextRunId);
      runRevealSequence(section, nextRunId).catch(() => {
        section.dataset.sneakyRevealActive = 'false';
      });
    },
    {
      threshold: INTERSECTION_THRESHOLDS,
      rootMargin: '0px 0px -12% 0px'
    }
  );

  observer.observe(section);
}

function boot() {
  document.querySelectorAll('[data-sneaky-good]').forEach((el) => {
    if (el instanceof HTMLElement) initSneakyGood(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

document.addEventListener('shopify:section:load', (event) => {
  const root = event.target;
  if (!(root instanceof HTMLElement)) return;
  if (root.matches('[data-sneaky-good]')) initSneakyGood(root);
  root.querySelectorAll('[data-sneaky-good]').forEach((el) => {
    if (el instanceof HTMLElement) initSneakyGood(el);
  });
});
